import { useEffect, useMemo, useRef, useState } from 'react';

import { isAbort, listAll, MAX_LIMIT } from './api';
import { isOpportunityId, type Opportunity } from './opportunity';

/**
 * Events by id, for everything the user's own lists point at.
 *
 * My Schedule, Save for Later, Not Interested and the user's edits all hold
 * ids, and Explore's feed only holds whatever pages the reader has scrolled
 * through — an event saved on Tuesday is rarely in the page loaded on
 * Thursday, and one that is over is never in it. So the directory asks for
 * them by id, past ones included, a hundred to a request.
 *
 * Asked for once per page load, which is what brings the next scrape's
 * corrections to the schedule, and again only for ids added since. An id the
 * API does not return — the listing was removed — simply has no record, and
 * stays in the user's list untouched, as it did when an event left
 * events.json.
 */

export interface OpportunityDirectory {
  byId: Map<string, Opportunity>;
  /** False until the ids held when the page loaded have been asked about. */
  ready: boolean;
}

export function useOpportunityDirectory(
  ids: readonly string[],
  /** Records already in hand, used until the directory's own copy arrives. */
  known: readonly Opportunity[],
): OpportunityDirectory {
  const [fetched, setFetched] = useState<Map<string, Opportunity>>(() => new Map());
  /** Asked for already, whatever the answer. */
  const asked = useRef(new Set<string>());
  const [pending, setPending] = useState(0);
  const [awaitingFirst, setAwaitingFirst] = useState(() => ids.some(isOpportunityId));
  /** Bumped when the connection comes back, to retry lookups that failed offline. */
  const [online, setOnline] = useState(0);

  const wanted = useMemo(() => [...new Set(ids)].filter(isOpportunityId), [ids]);

  useEffect(() => {
    const retry = () => setOnline((n) => n + 1);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  useEffect(() => {
    const queue = wanted.filter((id) => !asked.current.has(id));
    if (queue.length === 0) return;
    for (const id of queue) asked.current.add(id);

    for (let i = 0; i < queue.length; i += MAX_LIMIT) {
      const chunk = queue.slice(i, i + MAX_LIMIT);
      setPending((n) => n + 1);
      // Not aborted on cleanup: a list growing by one must not cancel the rest.
      listAll({ id: chunk, includePast: true })
        .then((items) =>
          setFetched((prev) => {
            const next = new Map(prev);
            for (const o of items) next.set(o.id, o);
            return next;
          }),
        )
        .catch((err: unknown) => {
          // Unreachable rather than absent: forget asking, so a retry can.
          if (!isAbort(err)) for (const id of chunk) asked.current.delete(id);
        })
        .finally(() => setPending((n) => n - 1));
    }
  }, [wanted, online]);

  useEffect(() => {
    if (pending === 0 && asked.current.size > 0) setAwaitingFirst(false);
  }, [pending]);

  const byId = useMemo(() => {
    const map = new Map(fetched);
    for (const o of known) if (!map.has(o.id)) map.set(o.id, o);
    return map;
  }, [fetched, known]);

  return { byId, ready: !awaitingFirst };
}
